/**
 * BlockV2 — pure render component.
 *
 * All state management, flows, events, and symbol resolution are handled
 * centrally by AppV2. BlockV2 simply renders the UI tree using the shared
 * ElementRegistry and the RenderContext provided by AppV2.
 *
 * NOTE: renderMode (meta.renderMode) is consumed by the SSR renderer only.
 * In CSR, BlockV2 renders ALL blocks identically regardless of renderMode —
 * the CSR renderer hydrates/renders everything interactively.
 * See: ssr-block-renderer.tsx for SSR-side renderMode branching.
 */

import type { BlockDef } from "@cogo/types-v2"
import { useContext } from "react"
import { RenderContext, renderUnmappedType } from "@cogo/react-renderer"
import { getSharedRegistry } from "./App"

export interface BlockV2Props {
  block: BlockDef
}

export function BlockV2({ block }: BlockV2Props) {
  const ctx = useContext(RenderContext)
  const registry = ctx?.registry ?? getSharedRegistry()
  const root = block.ui?.root
  if (!root) {
    // A MISSING root is a different condition from an UNMAPPED type, and gets
    // a deliberately different answer. Nothing was dropped and there is no
    // type to name, so no `data-unknown-type` marker is invented — a marker
    // without a type would be a fourth vocabulary for callers to learn, and
    // the gates that grep for it would start counting a condition they were
    // never written to measure. But the silence IS broken: `UiTree.root` is a
    // required field (@cogo/types-v2 schema/element.ts), so a block arriving
    // here without one is always an upstream defect, and a block that renders
    // nothing at all is indistinguishable from one that rendered an empty
    // container. Presentation of a legitimately-empty block belongs to the
    // host layer (BlockPlaceholder), not here.
    console.warn(`[BlockV2] block "${block.meta?.name ?? "(unnamed)"}" has no ui.root — rendering nothing`)
    return null
  }

  const ElementRenderer = registry.get(root.type)
  if (!ElementRenderer) {
    // Unmapped root type: warn + observable placeholder. Without this the
    // whole block vanished with no warning and no trace. Same declaration the
    // v1 ElementRenderer uses for leaf elements — one answer, not two.
    return renderUnmappedType(root.type, root.id)
  }

  return <ElementRenderer element={root as never} />
}
